// @flow
import type {
  FacebookResponse,
  FacebookUserData,
} from 'app/packages/pinterest-flow-types/authentication';
import { SCRIPT_TYPE, loadScriptWithWait } from 'app/packages/script-loader';

export const FACEBOOK_CONNECT = '_facebookConnect';

const isProd = process.env.NODE_ENV === 'production';
export const FACEBOOK_API_KEY = isProd ? '274266067164' : '360467581347';
const FACEBOOK_FIELDS = [
  'picture.type(large)',
  'id',
  'first_name',
  'last_name',
  'email',
  'gender',
  'birthday',
];
export const FACEBOOK_SCOPE = [
  'public_profile',
  'email',
  'user_likes',
  'user_birthday',
  'user_friends',
];

const getFBLocaleString = (contextLocale: string) => {
  let localeString = contextLocale;
  localeString = localeString.replace(/-/g, '_');
  switch (localeString) {
    case 'de':
      localeString = 'de_DE';
      break;
    case 'fr':
      localeString = 'fr_FR';
      break;
    case 'it':
      localeString = 'it_IT';
      break;
    case 'ja':
      localeString = 'ja_JP';
      break;
    case 'nl':
      localeString = 'nl_NL';
      break;
    case 'tr':
      localeString = 'tr_TR';
      break;
    case 'en_AU':
      localeString = 'en_US';
      break;
    case 'es_419':
      localeString = 'es_LA';
      break;
    case 'es_AR':
      localeString = 'es_LA';
      break;
    default:
      break;
  }
  return localeString;
};

export const initFB = (locale: string): Promise<any> => {
  const promise = loadScriptWithWait(
    `//connect.facebook.net/${getFBLocaleString(locale)}/sdk.js`,
    SCRIPT_TYPE.FACEBOOK,
  );
  return promise.then(() => {
    window.FB.init({
      appId: FACEBOOK_API_KEY,
      status: true,
      xfbml: true,
      version: 'v2.7',
    });
  });
};

export const getLoginStatus = (): Promise<FacebookResponse> =>
  new Promise((resolve) => {
    window.FB.getLoginStatus(resolve);
  });

export const handleLoginAttempt = (
  response: FacebookResponse,
  options?: { autologin?: boolean } = {},
  trackAction?: (type: string) => void,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
): Promise<*> =>
  new Promise((resolve, reject) => {
    if (response.status === 'connected') {
      const {
        authResponse: { userID, accessToken },
      } = response;
      const creds = {
        facebook_id: userID,
        facebook_token: accessToken,
        facebook_autologin: options.autologin || false,
      };

      const fields = FACEBOOK_FIELDS;
      const requestString = `/me?fields=${fields.join(',')}`;

      if (trackAction) {
        trackAction('login_status.connected');
      }

      window.FB.api(requestString, (response: FacebookUserData) => {
        resolve({
          creds,
          data: {
            ...response,
            ...creds,
            ...(response.picture && !response.picture.data.is_silhouette
              ? { image_url: response.picture.data.url }
              : {}),
          },
        });
      });
    } else {
      const status = response.status || 'unknown';
      if (trackAction) {
        trackAction(`login_status.${status}`);
      }
      reject({ status });
    }
  });

export const trackLoginButtonDialogOpen = (
  event: MessageEvent,
  trackAction?: (type: string) => void,
) => {
  const eventData = event.data;
  if (
    event.isTrusted &&
    typeof eventData === 'string' &&
    eventData.indexOf('_FB_') === 0 &&
    eventData.indexOf('type=login_button_dialog_open') !== -1 &&
    trackAction
  ) {
    trackAction('facebook_connect.login_button_dialog_open');
  }
};

// $FlowFixMe Flow v0.104.0 upgrade supression.
export const loginFB = (): Promise<*> => {
  const login = new Promise((resolve) => {
    window.FB.login(resolve, {
      scope: FACEBOOK_SCOPE.join(','),
    });
  });
  return login.then(handleLoginAttempt);
};

// $FlowFixMe Flow v0.104.0 upgrade supression.
export const checkFbPermission = (targetPerm: string): Promise<*> =>
  // Check if the current facebook user has granted permission for a certain field
  new Promise((resolve, reject) => {
    window.FB.api('/me/permissions', (perms_response) => {
      const permissions = perms_response.data;
      if (permissions) {
        const permissionFound = permissions.find(
          (permission) => permission.permission === targetPerm && permission.status === 'granted',
        );
        return resolve({
          hasPerm: !!permissionFound,
        });
      }
      return reject(Error('Failed to call facebook to get permission'));
    });
  });
